/**
 * Map View Persistence Utility
 *
 * Provides localStorage (fast) and Firestore (cross-device sync) persistence
 * for the user's last map viewport (latitude, longitude, zoom).
 */

import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from '../firebase/firebase';

const LOCAL_STORAGE_KEY = 'goldfinder_last_viewport';

// Default viewport - Western Australia mining region
export const DEFAULT_VIEWPORT = {
  latitude: -30.7490,
  longitude: 121.4660,
  zoom: 11
};

/**
 * Save viewport to localStorage (fast, synchronous)
 */
export function saveViewportToLocalStorage(viewport) {
  try {
    const data = {
      latitude: viewport.latitude,
      longitude: viewport.longitude,
      zoom: viewport.zoom,
      lastUpdated: Date.now()
    };
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.warn('Failed to save viewport to localStorage:', error);
  }
}

/**
 * Load viewport from localStorage
 * @returns {Object|null} viewport object or null if not found
 */
export function loadViewportFromLocalStorage() {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!stored) return null;

    const data = JSON.parse(stored);

    // Validate the data
    if (typeof data.latitude !== 'number' ||
        typeof data.longitude !== 'number' ||
        typeof data.zoom !== 'number') {
      return null;
    }

    return {
      latitude: data.latitude,
      longitude: data.longitude,
      zoom: data.zoom,
      lastUpdated: data.lastUpdated || 0
    };
  } catch (error) {
    console.warn('Failed to load viewport from localStorage:', error);
    return null;
  }
}

/**
 * Save viewport to Firestore (async, cross-device sync)
 */
export async function saveViewportToFirestore(viewport) {
  const userId = auth.currentUser?.uid;
  if (!userId) {
    console.warn('Cannot save viewport to Firestore: user not logged in');
    return;
  }

  try {
    const docRef = doc(db, "user_info", userId, "preferences", "map_viewport");
    await setDoc(docRef, {
      latitude: viewport.latitude,
      longitude: viewport.longitude,
      zoom: viewport.zoom,
      lastUpdated: serverTimestamp()
    });
  } catch (error) {
    console.warn('Failed to save viewport to Firestore:', error);
  }
}

/**
 * Load viewport from Firestore
 * @returns {Object|null} viewport object or null if not found
 */
export async function loadViewportFromFirestore() {
  const userId = auth.currentUser?.uid;
  if (!userId) {
    return null;
  }

  try {
    const docRef = doc(db, "user_info", userId, "preferences", "map_viewport");
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();

    // Validate the data
    if (typeof data.latitude !== 'number' ||
        typeof data.longitude !== 'number' ||
        typeof data.zoom !== 'number') {
      return null;
    }

    return {
      latitude: data.latitude,
      longitude: data.longitude,
      zoom: data.zoom,
      lastUpdated: data.lastUpdated?.toMillis?.() || 0
    };
  } catch (error) {
    console.warn('Failed to load viewport from Firestore:', error);
    return null;
  }
}

/**
 * Create a debounced function
 */
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Create a throttled function
 */
export function throttle(func, limit) {
  let inThrottle;
  let lastArgs;
  return function(...args) {
    lastArgs = args;
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
        if (lastArgs) {
          func(...lastArgs);
          lastArgs = null;
        }
      }, limit);
    }
  };
}
